import { makeAutoObservable } from "mobx";
import { failure, isOk, ok, pending, Result } from "@/common/common";
import {
  BooleanFieldModel,
  LocalDateFieldModel,
  NumberFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import {
  DCEvent,
  EventDispatcher,
  httpGet,
  httpPost,
  HttpResponse,
  navigate,
  showError,
  showGeneralError,
  showSuccess,
  UpdateStoreEvent,
} from "@/events/eventsDomain";
import {
  AddressForm,
  AddressType,
  addressUpdatedMessage,
  CompanyAddress,
  newAddressForm,
  toUpdateAddressRequest,
} from "@/companyAddress/companyAddressDomain";
import { settingsApi } from "@/overview/companySettings/companySettingsDomain";
import { FileUploadConfig, filterFileUploadConfig } from "@/fileUpload/fileUploadDomain";
import {
  adminEntityData,
  adminRoles,
  enterpriseAccountUrl,
  NavigationState,
  navigationState,
} from "@/utils/navigation";
import { toQueryString } from "@/utils/queryParams";
import { getAllCountriesSortedByName } from "@/utils/countries";
import { Language } from "@/i18n/languages";
import { formatNumber, FormatNumberOptions } from "@/utils/numbers";
import { Localized } from "@/companyRegistry/companyRegistryApiClient";
import { formatDate } from "@/i18n/dateFormat";
import { TFunction, TMessage, TMessageParam } from "@/i18n/tt";
import { IconName } from "@/components-v2/iconMapping";
import type { TagProps } from "@/components-v2/Tag";
import { CompanyStatus } from "@/types/CompanyInfo";
import { getRoleTypeLabel, RoleType } from "@/types/enums/RoleType";
import type { AccountRoleType } from "@/companyRoles/accountRoleTypeApiClient";
import { AppBarData } from "@/appBar/appBarDomain";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { isAxiosError } from "@/api/api";

export const entityDataTabs = ["all", "custom", "system", "not-set"] as const;
export type EntityDataTab = (typeof entityDataTabs)[number];

export const entityDataTabLabel = (tab: EntityDataTab, accountName?: string): TMessage | string => {
  switch (tab) {
    case "all":
      return { key: "entity-data.all-fields" };
    case "custom":
      return accountName ?? { key: "entity-data.origin-custom-any" };
    case "system":
      return { key: "entity-data.tab-system-fields" };
    case "not-set":
      return { key: "entity-data.not-set" };
  }
};

export type CustomFieldDataType =
  | "NUMBER"
  | "TEXT"
  | "DATE"
  | "SINGLE_SELECT"
  | "COUNTRY_CODE"
  | "CONTACT";

export type CustomFieldSelectOption = {
  id: string;
  label: string;
  isArchived: boolean;
};

export type CustomFieldValue =
  | { valueType: "NUMBER"; number: number }
  | { valueType: "TEXT"; text: string }
  | { valueType: "DATE"; date: string }
  | { valueType: "SINGLE_SELECT"; optionId: string }
  | { valueType: "COUNTRY_CODE"; code: string }
  | { valueType: "CONTACT"; userId: number; name: string };

export type CustomFieldContactRole = "ADMIN" | "BOARD_MEMBER" | "SHAREHOLDER";

export const customFieldContactRoles: CustomFieldContactRole[] = [
  "ADMIN",
  "BOARD_MEMBER",
  "SHAREHOLDER",
];

export const contactRoleLabels: { [key in CustomFieldContactRole]: TMessage } = {
  ADMIN: { key: "administrators" },
  BOARD_MEMBER: { key: "entity-data.role-board-members" },
  SHAREHOLDER: { key: "entity-data.role-shareholders" },
};

export const contactRoleNames: { [key in CustomFieldContactRole]: TMessage } = {
  ADMIN: { key: "entity-data.contact-role-admin" },
  BOARD_MEMBER: { key: "entity-data.contact-role-board-member" },
  SHAREHOLDER: { key: "entity-data.contact-role-shareholder" },
};

export type CustomFieldContact = {
  userId: number;
  name: string;
  isInCompany: boolean;
  companyCount: number;
};

export type CompanyCustomField = {
  definitionId: string;
  name: string;
  description?: string;
  dataType: CustomFieldDataType;
  selectOptions: CustomFieldSelectOption[];
  numberFormat?: CustomFieldNumberFormat;
  companyScope?: CustomFieldCompanyScope;
  validations?: CustomFieldValidations;
  commentRequiredFor?: string[];
  requiredLength?: number;
  limitToRole?: CustomFieldContactRole;
  value?: CustomFieldValue;
  effectiveFrom?: string;
  validThrough?: string;
  comment?: string;
  canEdit: boolean;
  updatedAt?: string;
  updatedBy?: string;
};

export const systemFieldKeys = [
  "COMPANY_NAME",
  "ORGANISATION_NUMBER",
  "COUNTRY",
  "SUBDIVISION",
  "ENTITY_TYPE",
  "COMPANY_STATUS",
  "BUSINESS_ADDRESS",
  "POSTAL_ADDRESS",
  "DATE_OF_INCORPORATION",
  "TIN_NUMBER",
  "ACCOUNTANT",
  "AUDITOR",
  "PARENT_COMPANY",
  "PARENT_DIRECT_OWNERSHIP",
  "PARENT_INTEGRATED_OWNERSHIP",
  "SUBSIDIARIES",
  "CHAIR",
  "BOARD",
  "CEO",
  "PORTFOLIO",
] as const;

export type SystemFieldKey = (typeof systemFieldKeys)[number];

export type SubsidiaryDetail = { name: string; ownershipInPercent?: number | null };

export type BoardMemberDetail = { name: string; role: RoleType };

export type IncorporationDateSource =
  | "INCORPORATION_TRANSACTION"
  | "BUSINESS_REGISTER"
  | "COMPANY_SETTINGS";

export type SystemFieldDetails =
  | {
      detailsType: "PARENT_COMPANY";
      parentShares?: number | null;
      totalShares?: number | null;
      directOwnership?: number | null;
      integratedOwnership?: number | null;
      votingPower?: number | null;
    }
  | { detailsType: "SUBSIDIARIES"; subsidiaries: SubsidiaryDetail[] }
  | {
      detailsType: "ROLE_HOLDER";
      email?: string | null;
      phoneNumber?: string | null;
      lastLogin?: string | null;
    }
  | { detailsType: "BOARD"; members: BoardMemberDetail[] }
  | { detailsType: "INCORPORATION_DATE"; source: IncorporationDateSource }
  | ({ detailsType: "ADDRESS" } & CompanyAddress);

export type CompanySystemField = {
  systemField: SystemFieldKey;
  value?: CustomFieldValue;
  details?: SystemFieldDetails | null;
  valueLabel?: Localized | null;
  isNotApplicable?: boolean;
  effectiveFrom?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
};

export const systemFieldLabels: { [key in SystemFieldKey]: TMessage } = {
  COMPANY_NAME: { key: "entity-data.system-company-name" },
  ORGANISATION_NUMBER: { key: "entity-data.system-organisation-number" },
  COUNTRY: { key: "entity-data.system-country" },
  SUBDIVISION: { key: "entity-data.system-subdivision" },
  ENTITY_TYPE: { key: "entity-data.system-entity-type" },
  COMPANY_STATUS: { key: "entity-data.system-company-status" },
  BUSINESS_ADDRESS: { key: "entity-data.system-business-address" },
  POSTAL_ADDRESS: { key: "entity-data.system-postal-address" },
  DATE_OF_INCORPORATION: { key: "entity-data.system-date-of-incorporation" },
  TIN_NUMBER: { key: "entity-data.system-tin-number" },
  ACCOUNTANT: { key: "entity-data.system-accountant" },
  AUDITOR: { key: "entity-data.system-auditor" },
  PARENT_COMPANY: { key: "entity-data.system-parent-company" },
  PARENT_DIRECT_OWNERSHIP: { key: "entity-data.system-parent-direct-ownership" },
  PARENT_INTEGRATED_OWNERSHIP: { key: "entity-data.system-parent-integrated-ownership" },
  SUBSIDIARIES: { key: "entity-data.system-subsidiaries" },
  CHAIR: { key: "entity-data.system-chair" },
  BOARD: { key: "entity-data.system-board" },
  CEO: { key: "entity-data.system-ceo" },
  PORTFOLIO: { key: "entity-data.system-portfolio" },
};

export const systemFieldValueParam: Partial<
  Record<SystemFieldKey, "country" | "org-no" | "companies" | "members" | "percent">
> = {
  COUNTRY: "country",
  ORGANISATION_NUMBER: "org-no",
  PARENT_DIRECT_OWNERSHIP: "percent",
  PARENT_INTEGRATED_OWNERSHIP: "percent",
  SUBSIDIARIES: "companies",
  BOARD: "members",
};

export const systemFieldCountMessage = (field: CompanySystemField): TMessage | undefined => {
  const param = systemFieldValueParam[field.systemField];
  if (field.value?.valueType !== "NUMBER" || (param !== "companies" && param !== "members")) {
    return undefined;
  }
  return {
    key:
      param === "companies"
        ? "entity-data.system-value-companies"
        : "entity-data.system-value-members",
    params: { count: field.value.number },
  };
};

export const systemFieldPercent = (field: CompanySystemField): TMessageParam | undefined =>
  systemFieldValueParam[field.systemField] === "percent" && field.value?.valueType === "NUMBER"
    ? { type: "percent", value: field.value.number }
    : undefined;

export const systemFieldTag = (
  field: CompanySystemField,
  locale: Language,
): TagProps | undefined =>
  field.systemField === "COMPANY_STATUS" && field.value?.valueType === "TEXT"
    ? {
        label: field.valueLabel?.[locale] ?? field.value.text,
        color: field.value.text === CompanyStatus.ACTIVE ? "success" : "neutral",
      }
    : undefined;

export const systemFieldDescriptions: { [key in SystemFieldKey]: TMessage } = {
  COMPANY_NAME: { key: "entity-data.system-description-company-name" },
  ORGANISATION_NUMBER: { key: "entity-data.system-description-organisation-number" },
  COUNTRY: { key: "entity-data.system-description-country" },
  SUBDIVISION: { key: "entity-data.system-description-subdivision" },
  ENTITY_TYPE: { key: "entity-data.system-description-entity-type" },
  COMPANY_STATUS: { key: "entity-data.system-description-company-status" },
  BUSINESS_ADDRESS: { key: "entity-data.system-description-business-address" },
  POSTAL_ADDRESS: { key: "entity-data.system-description-postal-address" },
  DATE_OF_INCORPORATION: { key: "entity-data.system-description-date-of-incorporation" },
  TIN_NUMBER: { key: "entity-data.system-description-tin-number" },
  ACCOUNTANT: { key: "entity-data.system-description-accountant" },
  AUDITOR: { key: "entity-data.system-description-auditor" },
  PARENT_COMPANY: { key: "entity-data.system-description-parent-company" },
  PARENT_DIRECT_OWNERSHIP: { key: "entity-data.system-description-parent-direct-ownership" },
  PARENT_INTEGRATED_OWNERSHIP: {
    key: "entity-data.system-description-parent-integrated-ownership",
  },
  SUBSIDIARIES: { key: "entity-data.system-description-subsidiaries" },
  CHAIR: { key: "entity-data.system-description-chair" },
  BOARD: { key: "entity-data.system-description-board" },
  CEO: { key: "entity-data.system-description-ceo" },
  PORTFOLIO: { key: "entity-data.system-description-portfolio" },
};

export type SystemFieldDetailRow = {
  id: string;
  label: TMessage;
  value: TMessage | TMessageParam;
};

const isSet = <T>(value: T | null | undefined): value is T => value !== undefined && value !== null;

const percentOrDash = (value?: number | null): TMessage | TMessageParam =>
  isSet(value) ? { type: "percent", value } : { translated: "–" };

const detailRow = (
  id: string,
  label: TMessage,
  value: TMessage | TMessageParam | undefined,
): SystemFieldDetailRow[] => (value ? [{ id, label, value }] : []);

const parentCompanyRows = (
  details: Extract<SystemFieldDetails, { detailsType: "PARENT_COMPANY" }>,
): SystemFieldDetailRow[] => [
  ...detailRow(
    "shares",
    { key: "entity-data.system-detail-parent-shares" },
    isSet(details.parentShares) && isSet(details.totalShares)
      ? {
          key: "entity-data.system-detail-shares-of-total",
          params: {
            shares: { type: "number", value: details.parentShares },
            total: { type: "number", value: details.totalShares },
          },
        }
      : undefined,
  ),
  ...detailRow(
    "direct",
    { key: "ownership.details.direct-ownership" },
    isSet(details.directOwnership)
      ? { type: "percent", value: details.directOwnership }
      : undefined,
  ),
  ...detailRow(
    "integrated",
    { key: "ownership.details.integrated-ownership" },
    isSet(details.integratedOwnership)
      ? { type: "percent", value: details.integratedOwnership }
      : undefined,
  ),
  ...detailRow(
    "voting",
    { key: "ownership.details.voting-power" },
    isSet(details.votingPower) ? { type: "percent", value: details.votingPower } : undefined,
  ),
];

const roleHolderRows = (
  details: Extract<SystemFieldDetails, { detailsType: "ROLE_HOLDER" }>,
): SystemFieldDetailRow[] => [
  ...detailRow(
    "email",
    { key: "email" },
    details.email ? { translated: details.email } : undefined,
  ),
  ...detailRow(
    "phone",
    { key: "entity-data.system-detail-phone-number" },
    details.phoneNumber ? { translated: details.phoneNumber } : undefined,
  ),
  ...detailRow(
    "last-login",
    { key: "last-login" },
    details.lastLogin ? { type: "datetime", value: details.lastLogin } : undefined,
  ),
];

const incorporationDateSourceLabels: { [key in IncorporationDateSource]: TMessage } = {
  INCORPORATION_TRANSACTION: { key: "entity-data.system-source-incorporation-transaction" },
  BUSINESS_REGISTER: { key: "entity-data.system-source-business-register" },
  COMPANY_SETTINGS: { key: "entity-data.system-source-company-settings" },
};

const boardRoleLabel = (role: RoleType): TMessage =>
  Object.values(RoleType).includes(role) ? getRoleTypeLabel(role) : { translated: "" };

export const systemFieldDetailRows = (field: CompanySystemField): SystemFieldDetailRow[] => {
  const details = field.details;
  if (!details) {
    return [];
  }
  switch (details.detailsType) {
    case "PARENT_COMPANY":
      return parentCompanyRows(details);
    case "SUBSIDIARIES":
      return details.subsidiaries.map((subsidiary, index) => ({
        id: `subsidiary-${index}`,
        label: { translated: subsidiary.name },
        value: percentOrDash(subsidiary.ownershipInPercent),
      }));
    case "ROLE_HOLDER":
      return roleHolderRows(details);
    case "BOARD":
      return details.members.map((member, index) => ({
        id: `member-${index}`,
        label: { translated: member.name },
        value: boardRoleLabel(member.role),
      }));
    case "INCORPORATION_DATE":
      return [
        {
          id: "source",
          label: { key: "entity-data.system-detail-source" },
          value: incorporationDateSourceLabels[details.source],
        },
      ];
    case "ADDRESS":
      return [];
  }
  return [];
};

const systemFieldAddressTypes: Partial<Record<SystemFieldKey, AddressType>> = {
  BUSINESS_ADDRESS: "BUSINESS",
  POSTAL_ADDRESS: "POSTAL",
};

export const systemFieldAddressType = (field: CompanySystemField): AddressType | undefined =>
  field.details?.detailsType === "ADDRESS" ? systemFieldAddressTypes[field.systemField] : undefined;

export const systemFieldDetailLinkUrl = (
  field: CompanySystemField,
  orgNo: string,
): string | undefined =>
  field.systemField === "CEO" && field.value ? adminRoles(orgNo) : undefined;

type EntityFieldRowCommon = {
  id: string;
  name: string;
  description?: string;
  updatedAt?: string;
  updatedBy?: string;
};

export type EntityFieldRow =
  | (EntityFieldRowCommon & { origin: "CUSTOM"; field: CompanyCustomField })
  | (EntityFieldRowCommon & { origin: "SYSTEM"; field: CompanySystemField });

export const customFieldDataTypes: CustomFieldDataType[] = [
  "TEXT",
  "NUMBER",
  "DATE",
  "SINGLE_SELECT",
  "COUNTRY_CODE",
  "CONTACT",
];

export const dataTypeLabels: { [key in CustomFieldDataType]: TMessage } = {
  TEXT: { key: "entity-data.type-text" },
  NUMBER: { key: "entity-data.type-number" },
  DATE: { key: "entity-data.type-date" },
  SINGLE_SELECT: { key: "entity-data.single-select" },
  COUNTRY_CODE: { key: "entity-data.type-country" },
  CONTACT: { key: "entity-data.type-contact" },
};

export const dataTypeIcons: { [key in CustomFieldDataType]: IconName } = {
  TEXT: "text-aa",
  NUMBER: "hash-straight",
  DATE: "calendar",
  SINGLE_SELECT: "caret-circle-down",
  COUNTRY_CODE: "globe",
  CONTACT: "user",
};

export const customFieldDateValidations = ["EFFECTIVE_FROM_DATE", "VALID_THROUGH_DATE"] as const;

export type CustomFieldDateValidation = (typeof customFieldDateValidations)[number];

export const customFieldValidations = [
  ...customFieldDateValidations,
  "COMMENT",
  "REQUIRED_LENGTH",
  "LIMIT_SELECTION_TO_ROLE",
] as const;

export type CustomFieldValidation = (typeof customFieldValidations)[number];

export const customFieldRequirements = ["OPTIONAL", "REQUIRED", "CONDITIONAL"] as const;

export type CustomFieldRequirement = (typeof customFieldRequirements)[number];

export type CustomFieldValidations = Partial<Record<CustomFieldValidation, CustomFieldRequirement>>;

export const commentMaxLength = 100;

export type CustomFieldSeparators = "NONE";

export type CustomFieldNumberFormat = {
  decimalPlaces: number;
  separators?: CustomFieldSeparators;
};

export const decimalPlaceOptions = [0, 1, 2];

export const defaultNumberFormat: CustomFieldNumberFormat = {
  decimalPlaces: 0,
  separators: "NONE",
};

export const numberFormatOptions = (format: CustomFieldNumberFormat): FormatNumberOptions => ({
  minimumFractionDigits: format.decimalPlaces,
  maximumFractionDigits: format.decimalPlaces,
  useGrouping: format.separators !== "NONE",
});

export const formatCustomNumber = (
  lang: Language,
  value: number,
  format: CustomFieldNumberFormat = defaultNumberFormat,
): string => formatNumber(lang, value, numberFormatOptions(format));

export const numberFormatSample = (lang: Language, format: CustomFieldNumberFormat): string =>
  formatCustomNumber(lang, 1000000, format);

export const numberDigitCount = (
  value: number,
  format: CustomFieldNumberFormat = defaultNumberFormat,
): number => Math.abs(value).toFixed(format.decimalPlaces).replace(/\D/g, "").length;

export const requiredLengthMessage = (requiredLength: number): TMessage => ({
  key: "entity-data.required-length-error",
  params: { length: requiredLength },
});

export const requiredLengthSample = (
  requiredLength: number,
  format: CustomFieldNumberFormat,
): number => {
  const digits = Array.from({ length: requiredLength }, (_, index) => String((index % 9) + 1)).join(
    "",
  );
  return Number(digits) / 10 ** format.decimalPlaces;
};

export type RequiredLengthIssue = { level: "error" | "warning"; message: TMessage };

export const requiredLengthIssue = (
  format: CustomFieldNumberFormat,
  requiredLength: number | undefined,
): RequiredLengthIssue | undefined => {
  if (requiredLength === undefined || requiredLength < 1) {
    return undefined;
  }
  const decimals = format.decimalPlaces;
  if (requiredLength <= decimals) {
    return {
      level: "error",
      message: {
        key: "entity-data.required-length-impossible",
        params: { length: requiredLength, decimals, min: decimals + 1 },
      },
    };
  }
  if (decimals === 0 && format.separators === "NONE") {
    return undefined;
  }
  return {
    level: "warning",
    message: {
      key: "entity-data.required-length-example",
      params: {
        example: {
          type: "number",
          value: requiredLengthSample(requiredLength, format),
          ...numberFormatOptions(format),
        },
      },
    },
  };
};

export const validationLabels: { [key in CustomFieldValidation]: TMessage } = {
  EFFECTIVE_FROM_DATE: { key: "entity-data.validation-effective-from" },
  VALID_THROUGH_DATE: { key: "entity-data.validation-valid-through" },
  COMMENT: { key: "entity-data.comment" },
  REQUIRED_LENGTH: { key: "entity-data.validation-required-length" },
  LIMIT_SELECTION_TO_ROLE: { key: "entity-data.validation-limit-to-role" },
};

export const validationIcons: { [key in CustomFieldValidation]: IconName } = {
  EFFECTIVE_FROM_DATE: "calendar",
  VALID_THROUGH_DATE: "calendar-check",
  COMMENT: "chat-text",
  REQUIRED_LENGTH: "faders-horizontal",
  LIMIT_SELECTION_TO_ROLE: "funnel",
};

export const valueDateLabels: { [key in CustomFieldDateValidation]: TMessage } = {
  EFFECTIVE_FROM_DATE: { key: "entity-data.effective-date" },
  VALID_THROUGH_DATE: { key: "entity-data.valid-through" },
};

export const valueDateFieldLabels: { [key in CustomFieldDateValidation]: TMessage } = {
  EFFECTIVE_FROM_DATE: { key: "entity-data.effective-from" },
  VALID_THROUGH_DATE: { key: "entity-data.valid-through" },
};

export const requirementLabels: { [key in CustomFieldRequirement]: TMessage } = {
  OPTIONAL: { key: "optional" },
  REQUIRED: { key: "required" },
  CONDITIONAL: { key: "conditional" },
};

export const requirementsFor = (validation: CustomFieldValidation): CustomFieldRequirement[] => {
  switch (validation) {
    case "COMMENT":
      return [...customFieldRequirements];
    case "REQUIRED_LENGTH":
    case "LIMIT_SELECTION_TO_ROLE":
      return ["REQUIRED"];
    case "EFFECTIVE_FROM_DATE":
    case "VALID_THROUGH_DATE":
      return customFieldRequirements.filter((requirement) => requirement !== "CONDITIONAL");
  }
};

export type ValidationEntry = {
  validation: CustomFieldValidation;
  requirement: CustomFieldRequirement;
};

export const validationEntries = (validations: CustomFieldValidations = {}): ValidationEntry[] =>
  customFieldValidations.flatMap((validation) => {
    const requirement = validations[validation];
    return requirement ? [{ validation, requirement }] : [];
  });

export type DateValidationEntry = {
  validation: CustomFieldDateValidation;
  requirement: CustomFieldRequirement;
};

export const dateValidationEntries = (
  validations: CustomFieldValidations = {},
): DateValidationEntry[] =>
  customFieldDateValidations.flatMap((validation) => {
    const requirement = validations[validation];
    return requirement ? [{ validation, requirement }] : [];
  });

export type CompanyCustomFieldsResponse = {
  accountId?: string;
  subject?: string;
  canManageFields: boolean;
  systemFields: CompanySystemField[];
  fields: CompanyCustomField[];
};

export type CustomFieldCompanyScope = "ALL_COMPANIES" | "SELECTED_COMPANIES" | "HIDDEN";

export type CustomFieldObjectType = "COMPANY" | "COMPANY_ROLE";

export type CustomFieldSubjectItem =
  | { objectType: "COMPANY"; subject: string; orgNo: string; companyName: string }
  | {
      objectType: "COMPANY_ROLE";
      subject: string;
      orgNo: string;
      companyName: string;
      companyroleId: number;
      name: string;
      role: string;
      accountRoleType?: AccountRoleType;
    };

export const subjectDisplayName = (item: CustomFieldSubjectItem): string =>
  item.objectType === "COMPANY" ? item.companyName : `${item.name} (${item.companyName})`;

const isRoleType = (role: string): role is RoleType =>
  Object.values<string>(RoleType).includes(role);

export const subjectRoleLabel = (role: string, accountRoleType?: AccountRoleType): TMessage => {
  if (accountRoleType) return { translated: accountRoleType.name };
  return isRoleType(role) ? getRoleTypeLabel(role) : { translated: role };
};

export type AccountCompanyOption = { orgNo: string; name: string };

export type CustomFieldGrantee = "ACCOUNT_ADMIN" | "ADMIN" | "BOARD_MEMBER" | "SHAREHOLDER";

export type CustomFieldPermission = "NONE" | "VIEW" | "EDIT";

export type CustomFieldDefinition = {
  id: string;
  name: string;
  description?: string;
  dataType: CustomFieldDataType;
  objectType: CustomFieldObjectType;
  selectOptions: CustomFieldSelectOption[];
  numberFormat?: CustomFieldNumberFormat;
  companyScope: CustomFieldCompanyScope;
  selectedOrgNos: string[];
  permissions: Partial<Record<CustomFieldGrantee, CustomFieldPermission>>;
  validations?: CustomFieldValidations;
  commentRequiredFor?: string[];
  requiredLength?: number;
  limitToRole?: CustomFieldContactRole;
  archivedAt?: string;
};

export type CustomFieldEventPayload =
  | {
      payloadType: "VALUE_SET";
      value?: CustomFieldValue;
      effectiveFrom?: string;
      validThrough?: string;
      comment?: string;
    }
  | { payloadType: "FIELD_CREATED" }
  | { payloadType: "FIELD_RENAMED"; name: string }
  | { payloadType: "DESCRIPTION_CHANGED"; description?: string }
  | {
      payloadType: "DATA_TYPE_CHANGED";
      dataType: CustomFieldDataType;
      selectOptions: CustomFieldSelectOption[];
    }
  | { payloadType: "OPTION_ADDED"; option: CustomFieldSelectOption }
  | { payloadType: "OPTION_RELABELLED"; optionId: string; label: string }
  | { payloadType: "OPTION_ARCHIVED"; optionId: string }
  | { payloadType: "OPTION_RESTORED"; optionId: string }
  | { payloadType: "OPTION_REMOVED"; optionId: string }
  | { payloadType: "OPTIONS_REORDERED"; optionIds: string[] }
  | { payloadType: "NUMBER_FORMAT_CHANGED"; numberFormat?: CustomFieldNumberFormat }
  | {
      payloadType: "VALIDATIONS_CHANGED";
      validations: CustomFieldValidations;
      commentRequiredFor?: string[];
      requiredLength?: number;
      limitToRole?: CustomFieldContactRole;
    }
  | {
      payloadType: "PERMISSIONS_CHANGED";
      permissions: Partial<Record<CustomFieldGrantee, CustomFieldPermission>>;
    }
  | {
      payloadType: "ACTIVATION_CHANGED";
      companyScope: CustomFieldCompanyScope;
      selectedOrgNos: string[];
    }
  | { payloadType: "FIELD_ARCHIVED" };

export type CustomFieldEventEntry = {
  id: string;
  eventType: string;
  orgNo?: string;
  subject?: string;
  recordedAt: string;
  actor: { id: string; name: string; email?: string };
  payload: CustomFieldEventPayload;
};

export type EntityDataHistoryState = {
  field: CompanyCustomField;
  result: Result<CustomFieldEventEntry[]>;
};

export type EntityPanelTab = "field" | "log";

export type CustomFieldPanelState = {
  origin: "CUSTOM";
  definitionId: string;
  tab: EntityPanelTab;
  logResult: Result<CustomFieldEventEntry[]>;
};

export type SystemFieldLogEntry = {
  id: string;
  effectiveFrom?: string;
  recordedAt: string;
  from?: string;
  to?: string;
  actorName: string;
  fromLabel?: Localized | null;
  toLabel?: Localized | null;
};

export type SystemFieldPanelState = {
  origin: "SYSTEM";
  systemField: SystemFieldKey;
  tab: EntityPanelTab;
  logResult: Result<SystemFieldLogEntry[]>;
};

export type EntityFieldPanelState = CustomFieldPanelState | SystemFieldPanelState;

export type EntityDataState = {
  orgNo: string;
  subject?: string;
  fieldsResult: Result<CompanyCustomField[]>;
  systemFields: CompanySystemField[];
  accountId?: string;
  canManageFields: boolean;
  tab: EntityDataTab;
  searchModel: StringFieldModel;
  editModel?: EntityValueFormModel;
  addressForm?: AddressForm;
  history?: EntityDataHistoryState;
  fieldPanel?: EntityFieldPanelState;
  uploadConfig?: FileUploadConfig;
  isImporting?: boolean;
  importResult?: CustomFieldsImportResult;
};

export const initEntityDataPage = ({ orgNo }: { orgNo: string }): DCEvent[] => [
  { type: "update-store", data: { entityData: defaultEntityDataState(orgNo) } },
  fetchFields(orgNo),
  fetchUploadConfig(orgNo),
];

export const defaultEntityDataState = (orgNo: string): EntityDataState => ({
  orgNo,
  fieldsResult: pending(),
  systemFields: [],
  canManageFields: false,
  tab: "all",
  searchModel: new StringFieldModel(),
});

const updateEntityData = (partial: Partial<EntityDataState>): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    entityData: (entityData) =>
      entityData && {
        ...entityData,
        ...partial,
      },
  },
});

const fetchFields = (orgNo: string): DCEvent =>
  httpGet({
    url: `/custom-fields/${orgNo}`,
    onSuccess: (res: HttpResponse<CompanyCustomFieldsResponse>) => [
      updateEntityData({
        subject: res.data.subject,
        fieldsResult: ok(res.data.fields),
        systemFields: res.data.systemFields,
        accountId: res.data.accountId,
        canManageFields: res.data.canManageFields,
      }),
    ],
    onFailure: () => [updateEntityData({ fieldsResult: failure() })],
  });

export const exportEntityDataToExcel = (orgNo: string): DCEvent => ({
  type: "download-file",
  url: `/custom-fields/${orgNo}/export`,
});

const fetchUploadConfig = (orgNo: string): DCEvent =>
  httpGet({
    url: `/custom-fields/${orgNo}/file-upload-config`,
    onSuccess: (res: HttpResponse<FileUploadConfig>) => [
      updateEntityData({ uploadConfig: filterFileUploadConfig(res.data, "xlsx") }),
    ],
    onFailure: () => [],
  });

export const uploadEntityDataExcel = (state: EntityDataState, files: File[] | null): DCEvent[] => {
  const file = files?.[0];
  if (!file) {
    return [];
  }
  const data = new FormData();
  data.append("file", file);
  return [
    updateEntityData({ isImporting: true, importResult: undefined }),
    httpPost({
      url: `/custom-fields/${state.orgNo}/import-excel`,
      data,
      onSuccess: (res: HttpResponse<CustomFieldsImportResult>) => [
        updateEntityData({ isImporting: false, importResult: res.data }),
        fetchFields(state.orgNo),
      ],
      onFailure: () => [
        updateEntityData({ isImporting: false }),
        showError({ key: "entity-data.upload-error" }),
      ],
    }),
  ];
};

const systemRowId = (key: SystemFieldKey): string => `system:${key}`;

const matchesTab = (row: EntityFieldRow, tab: EntityDataTab): boolean => {
  switch (tab) {
    case "all":
      return true;
    case "custom":
      return row.origin === "CUSTOM";
    case "system":
      return row.origin === "SYSTEM";
    case "not-set":
      return row.field.value === undefined || row.field.value === null;
  }
};

export const entityFieldRows = (
  state: EntityDataState,
  t: TFunction,
  lang: Language,
): EntityFieldRow[] => {
  const query = state.searchModel.inputValue.trim().toLowerCase();
  const systemRows: EntityFieldRow[] = state.systemFields.map((field) => ({
    origin: "SYSTEM",
    id: systemRowId(field.systemField),
    name: t(systemFieldLabels[field.systemField]),
    description: t(systemFieldDescriptions[field.systemField]),
    updatedAt: field.updatedAt ?? undefined,
    updatedBy: field.updatedBy ?? undefined,
    field,
  }));
  const customRows: EntityFieldRow[] = isOk(state.fieldsResult)
    ? state.fieldsResult.data.map((field) => ({
        origin: "CUSTOM",
        id: field.definitionId,
        name: field.name,
        description: field.description,
        updatedAt: field.updatedAt,
        updatedBy: field.updatedBy,
        field,
      }))
    : [];
  return [...systemRows, ...customRows]
    .filter((row) => matchesTab(row, state.tab))
    .filter((row) => query === "" || row.name.toLowerCase().includes(query))
    .toSorted((a, b) => a.name.localeCompare(b.name, lang));
};

export const sameFieldValue = (
  a?: CustomFieldValue | null,
  b?: CustomFieldValue | null,
): boolean => {
  if (!a || !b) {
    return !a && !b;
  }
  switch (a.valueType) {
    case "TEXT":
      return b.valueType === "TEXT" && a.text === b.text;
    case "NUMBER":
      return b.valueType === "NUMBER" && a.number === b.number;
    case "DATE":
      return b.valueType === "DATE" && a.date === b.date;
    case "SINGLE_SELECT":
      return b.valueType === "SINGLE_SELECT" && a.optionId === b.optionId;
    case "COUNTRY_CODE":
      return b.valueType === "COUNTRY_CODE" && a.code === b.code;
    case "CONTACT":
      return b.valueType === "CONTACT" && a.userId === b.userId;
  }
};

export const configureFieldsUrl = (
  state: EntityDataState,
  definitionId?: string,
): string | undefined =>
  state.canManageFields && state.accountId
    ? `${enterpriseAccountUrl(state.accountId)}/fields${toQueryString({ field: definitionId })}`
    : undefined;

export const configureFieldsNavigation = (state: EntityDataState): NavigationState =>
  navigationState({ referrer: adminEntityData(state.orgNo) });

export const selectOptionLabel = (
  selectOptions: CustomFieldSelectOption[],
  optionId: string,
): string | undefined => selectOptions.find((option) => option.id === optionId)?.label;

export const definitionAppliesTo = (definition: CustomFieldDefinition, orgNo: string): boolean => {
  switch (definition.companyScope) {
    case "ALL_COMPANIES":
      return true;
    case "SELECTED_COMPANIES":
      return definition.selectedOrgNos.includes(orgNo);
    case "HIDDEN":
      return false;
  }
};

export type StoredValueEntry = {
  value?: CustomFieldValue;
  effectiveFrom?: string;
  validThrough?: string;
  comment?: string;
};

export const toCompanyCustomField = (
  definition: CustomFieldDefinition,
  entry: StoredValueEntry = {},
): CompanyCustomField => ({
  definitionId: definition.id,
  name: definition.name,
  description: definition.description,
  dataType: definition.dataType,
  selectOptions: definition.selectOptions,
  numberFormat: definition.numberFormat,
  validations: definition.validations,
  commentRequiredFor: definition.commentRequiredFor,
  requiredLength: definition.requiredLength,
  limitToRole: definition.limitToRole,
  value: entry.value,
  effectiveFrom: entry.effectiveFrom,
  validThrough: entry.validThrough,
  comment: entry.comment,
  canEdit: true,
});

export const commandError = (err: unknown): TMessage => {
  if (isAxiosError(err)) {
    const data: unknown = err.response?.data;
    if (
      typeof data === "object" &&
      data !== null &&
      "message" in data &&
      typeof data.message === "string"
    ) {
      return { translated: data.message };
    }
  }
  return { key: "general-error" };
};

type CountryOption = { code: string; name: string };

export type CustomFieldValueInput = {
  value: CustomFieldValue | null;
  effectiveFrom?: string;
  validThrough?: string;
  comment?: string;
};

export const setValueCommand = (
  definitionId: string,
  subject: string,
  input: CustomFieldValueInput,
) => ({
  commandType: "SET_VALUE" as const,
  definitionId,
  subject,
  ...input,
});

export const valueDate = (
  field: Pick<CompanyCustomField, "effectiveFrom" | "validThrough">,
  validation: CustomFieldDateValidation,
): string | undefined =>
  validation === "EFFECTIVE_FROM_DATE" ? field.effectiveFrom : field.validThrough;

export const valueDateFieldLabel = (entry: DateValidationEntry): TMessage => ({
  key:
    entry.requirement === "REQUIRED" ? "entity-data.label-required" : "entity-data.label-optional",
  params: { label: valueDateFieldLabels[entry.validation] },
});

export const valueText = (
  field: Pick<CompanyCustomField, "selectOptions">,
  value: CustomFieldValue | null,
): string | undefined => {
  if (!value) {
    return undefined;
  }
  switch (value.valueType) {
    case "TEXT":
      return value.text;
    case "NUMBER":
      return String(value.number);
    case "DATE":
      return value.date;
    case "SINGLE_SELECT":
      return selectOptionLabel(field.selectOptions, value.optionId);
    case "COUNTRY_CODE":
      return value.code;
    case "CONTACT":
      return value.name;
  }
};

const valueParam = (
  field: CompanyCustomField,
  value: CustomFieldValue | null,
): TMessage | TMessageParam | string => {
  if (!value) {
    return { key: "entity-data.not-set" };
  }
  switch (value.valueType) {
    case "TEXT":
      return value.text;
    case "NUMBER":
      return { type: "number", value: value.number };
    case "DATE":
      return { type: "date", value: value.date };
    case "SINGLE_SELECT":
      return selectOptionLabel(field.selectOptions, value.optionId) ?? "–";
    case "COUNTRY_CODE":
      return { type: "country", code: value.code };
    case "CONTACT":
      return value.name;
  }
};

export const overlapWarning = (
  field: CompanyCustomField,
  input: CustomFieldValueInput,
): TMessage | undefined => {
  const effectiveFrom = input.effectiveFrom;
  if (!effectiveFrom || effectiveFrom === field.effectiveFrom) {
    return undefined;
  }
  const previousThrough = field.validThrough;
  if (!previousThrough || effectiveFrom > previousThrough) {
    return undefined;
  }
  return {
    key: "entity-data.overlap-warning",
    params: {
      from: { type: "date", value: effectiveFrom },
      through: { type: "date", value: previousThrough },
      value: valueParam(field, input.value),
    },
  };
};

export const contactSearchPlaceholder = (
  field: Pick<CompanyCustomField, "limitToRole">,
): TMessage =>
  field.limitToRole
    ? {
        key: "entity-data.contact-search-role",
        params: { role: contactRoleNames[field.limitToRole] },
      }
    : { key: "entity-data.contact-search" };

export const contactWarning = (
  field: Pick<CompanyCustomField, "limitToRole">,
  contact: CustomFieldContact | null,
  companyName?: string,
): TMessage | undefined => {
  const role = field.limitToRole;
  if (!role || !contact || contact.isInCompany || companyName === undefined) {
    return undefined;
  }
  return {
    key: "entity-data.contact-not-in-company",
    params: {
      name: contact.name,
      role: contactRoleNames[role],
      count: contact.companyCount,
      company: companyName,
    },
  };
};

export class EntityValueFormModel {
  readonly text: StringFieldModel;
  readonly number: NumberFieldModel;
  readonly date: LocalDateFieldModel;
  readonly option: RadioFieldModel<CustomFieldSelectOption | null>;
  readonly country: RadioFieldModel<CountryOption | null>;
  readonly effectiveFrom: LocalDateFieldModel;
  readonly validThrough: LocalDateFieldModel;
  readonly comment: StringFieldModel;
  contact: CustomFieldContact | null;
  contacts: CustomFieldContact[];
  isLoadingContacts: boolean;
  error?: TMessage = undefined;
  isSubmitting = false;

  constructor(
    readonly field: CompanyCustomField,
    language: Language,
  ) {
    const value = field.value;
    this.effectiveFrom = new LocalDateFieldModel({
      value: field.effectiveFrom,
      required: field.validations?.EFFECTIVE_FROM_DATE === "REQUIRED",
    });
    this.validThrough = new LocalDateFieldModel({
      value: field.validThrough,
      required: field.validations?.VALID_THROUGH_DATE === "REQUIRED",
    });
    this.comment = new StringFieldModel({ value: field.comment ?? "" });
    this.text = new StringFieldModel({
      value: value?.valueType === "TEXT" ? value.text : "",
    });
    const numberFormat = field.numberFormat;
    const requiredLength = field.requiredLength;
    this.number = new NumberFieldModel({
      value: value?.valueType === "NUMBER" ? value.number : undefined,
      isInteger: numberFormat?.decimalPlaces === 0,
      minDecimals: numberFormat?.decimalPlaces,
      maxDecimals: numberFormat?.decimalPlaces,
      disableFormatting: numberFormat?.separators === "NONE",
      validator:
        requiredLength === undefined
          ? undefined
          : (number) =>
              number === undefined || numberDigitCount(number, numberFormat) === requiredLength
                ? undefined
                : requiredLengthMessage(requiredLength),
    });
    this.date = new LocalDateFieldModel({
      value: value?.valueType === "DATE" ? value.date : undefined,
    });
    const selectedId = value?.valueType === "SINGLE_SELECT" ? value.optionId : undefined;
    const selectable = field.selectOptions.filter(
      (option) => !option.isArchived || option.id === selectedId,
    );
    this.option = new RadioFieldModel<CustomFieldSelectOption | null>({
      options: [null, ...selectable],
      idFn: (option) => option?.id ?? "not-set",
      labelFn: (option) => (option ? { translated: option.label } : { key: "entity-data.not-set" }),
      value: selectable.find((option) => option.id === selectedId) ?? null,
    });
    const countryOptions = getAllCountriesSortedByName(language);
    const selectedCode = value?.valueType === "COUNTRY_CODE" ? value.code : undefined;
    this.country = new RadioFieldModel<CountryOption | null>({
      options: [null, ...countryOptions],
      idFn: (option) => option?.code ?? "not-set",
      labelFn: (option) => (option ? { translated: option.name } : { key: "entity-data.not-set" }),
      value: countryOptions.find((option) => option.code === selectedCode) ?? null,
    });
    this.contact =
      value?.valueType === "CONTACT"
        ? { userId: value.userId, name: value.name, isInCompany: true, companyCount: 0 }
        : null;
    this.contacts = this.contact ? [this.contact] : [];
    this.isLoadingContacts = field.dataType === "CONTACT";
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setError(error?: TMessage) {
    this.error = error;
  }

  setContact(contact: CustomFieldContact | null) {
    this.contact = contact;
  }

  setContacts(contacts: CustomFieldContact[]) {
    const selected = this.contact;
    this.contacts =
      selected && !contacts.some((candidate) => candidate.userId === selected.userId)
        ? [selected, ...contacts]
        : contacts;
    this.contact = this.contacts.find((c) => c.userId === selected?.userId) ?? null;
    this.isLoadingContacts = false;
  }

  setSubmitting(isSubmitting: boolean) {
    this.isSubmitting = isSubmitting;
  }

  dateModel(validation: CustomFieldDateValidation): LocalDateFieldModel {
    return validation === "EFFECTIVE_FROM_DATE" ? this.effectiveFrom : this.validThrough;
  }

  get isValid(): boolean {
    return [
      this.text,
      this.number,
      this.date,
      this.option,
      this.country,
      this.effectiveFrom,
      this.validThrough,
      this.comment,
    ].every((model) => model.isValid);
  }

  get isCommentRequired(): boolean {
    const requirement = this.field.validations?.COMMENT;
    if (requirement === "REQUIRED") {
      return true;
    }
    if (requirement !== "CONDITIONAL") {
      return false;
    }
    const text = valueText(this.field, this.toValue());
    return (
      text !== undefined &&
      (this.field.commentRequiredFor ?? []).some(
        (trigger) => trigger.toLowerCase() === text.toLowerCase(),
      )
    );
  }

  validate(): boolean {
    const isValueValid = this.validateValue();
    if (this.toValue() === null) {
      return isValueValid;
    }
    const areDatesValid = dateValidationEntries(this.field.validations)
      .map((entry) => {
        const model = this.dateModel(entry.validation);
        model.commit();
        return model.validate();
      })
      .every(Boolean);
    return isValueValid && areDatesValid && this.validateComment();
  }

  private validateComment(): boolean {
    this.comment.commit();
    if (!this.field.validations?.COMMENT) {
      this.comment.setError(undefined);
      return true;
    }
    const comment = this.comment.inputValue.trim();
    if (comment.length > commentMaxLength) {
      this.comment.setError({
        key: "entity-data.comment-too-long",
        params: { max: commentMaxLength },
      });
      return false;
    }
    if (comment === "" && this.isCommentRequired) {
      this.comment.setError({
        key: "entity-data.comment-required",
        params: { value: valueText(this.field, this.toValue()) ?? "" },
      });
      return false;
    }
    this.comment.setError(undefined);
    return true;
  }

  toInput(): CustomFieldValueInput {
    const value = this.toValue();
    if (value === null) {
      return { value };
    }
    const validations = this.field.validations ?? {};
    return {
      value,
      effectiveFrom: validations.EFFECTIVE_FROM_DATE
        ? this.effectiveFrom.validatedValue
        : undefined,
      validThrough: validations.VALID_THROUGH_DATE ? this.validThrough.validatedValue : undefined,
      comment: validations.COMMENT ? this.comment.inputValue.trim() || undefined : undefined,
    };
  }

  private validateValue(): boolean {
    switch (this.field.dataType) {
      case "NUMBER": {
        this.number.commit();
        return this.number.validate();
      }
      case "DATE": {
        this.date.commit();
        return this.date.validate();
      }
      case "TEXT":
      case "SINGLE_SELECT":
      case "COUNTRY_CODE":
      case "CONTACT":
        return true;
    }
  }

  toValue(): CustomFieldValue | null {
    switch (this.field.dataType) {
      case "TEXT": {
        const text = this.text.inputValue.trim();
        return text === "" ? null : { valueType: "TEXT", text };
      }
      case "NUMBER": {
        const number = this.number.toNumber();
        return number === undefined ? null : { valueType: "NUMBER", number };
      }
      case "DATE": {
        const date = this.date.validatedValue;
        return date === undefined || date === "" ? null : { valueType: "DATE", date };
      }
      case "SINGLE_SELECT": {
        const option = this.option.value;
        return option === null ? null : { valueType: "SINGLE_SELECT", optionId: option.id };
      }
      case "COUNTRY_CODE": {
        const country = this.country.value;
        return country === null ? null : { valueType: "COUNTRY_CODE", code: country.code };
      }
      case "CONTACT": {
        const contact = this.contact;
        return contact === null
          ? null
          : { valueType: "CONTACT", userId: contact.userId, name: contact.name };
      }
    }
  }
}

export type AddFieldOption = { id: string; optionId?: string; label: StringFieldModel };

export type AddFieldValidationRow = {
  id: string;
  validation: RadioFieldModel<CustomFieldValidation>;
  requirement: RadioFieldModel<CustomFieldRequirement>;
};

export type AddFieldTab = "setup" | "validation" | "permissions" | "activation";

export const permissionLabels: { [key in CustomFieldPermission]: TMessage } = {
  EDIT: { key: "permissions.can-edit" },
  VIEW: { key: "permissions.can-view" },
  NONE: { key: "entity-data.permission-none" },
};

const permissionFieldModel = (
  value: CustomFieldPermission,
): RadioFieldModel<CustomFieldPermission> =>
  new RadioFieldModel<CustomFieldPermission>({
    value,
    options: ["EDIT", "VIEW", "NONE"],
    idFn: (permission) => permission,
    labelFn: (permission) => permissionLabels[permission],
  });

const ownerFieldModel = (): RadioFieldModel<CustomFieldPermission> =>
  new RadioFieldModel<CustomFieldPermission>({
    value: "EDIT",
    options: ["EDIT"],
    idFn: (permission) => permission,
    labelFn: () => ({ key: "entity-data.permission-owner" }),
  });

export class AddFieldFormModel {
  readonly title: StringFieldModel;
  readonly description: StringFieldModel;
  readonly accountAdminPermission: RadioFieldModel<CustomFieldPermission>;
  readonly adminPermission: RadioFieldModel<CustomFieldPermission>;
  readonly boardMemberPermission: RadioFieldModel<CustomFieldPermission>;
  readonly shareholderPermission: RadioFieldModel<CustomFieldPermission>;
  readonly companyScope: RadioFieldModel<CustomFieldCompanyScope>;
  readonly companySelection: OptionFieldModel<AccountCompanyOption>;
  readonly commentRequiredFor: StringFieldModel;
  readonly decimalPlaces: RadioFieldModel<number>;
  readonly thousandsSeparator: BooleanFieldModel;
  readonly requiredLength: NumberFieldModel;
  readonly limitToRole: RadioFieldModel<CustomFieldContactRole | undefined>;
  activeTab: AddFieldTab = "setup";
  showDescription: boolean;
  validationRows: AddFieldValidationRow[] = [];
  options: AddFieldOption[] = [];
  focusOptionId?: string = undefined;
  error?: TMessage = undefined;
  isSubmitting = false;
  private nextOptionId = 0;
  private nextValidationId = 0;
  private readonly archivedOptions: CustomFieldSelectOption[];

  constructor(
    readonly dataType: CustomFieldDataType,
    readonly definition?: CustomFieldDefinition,
    readonly companies: AccountCompanyOption[] = [],
    readonly objectType: CustomFieldObjectType = definition?.objectType ?? "COMPANY",
  ) {
    this.title = new StringFieldModel({ required: true, value: definition?.name ?? "" });
    this.description = new StringFieldModel({ value: definition?.description ?? "" });
    this.companySelection = new OptionFieldModel<AccountCompanyOption>({
      options: companies,
      idFn: (company) => company.orgNo,
      labelFn: (company) => ({ translated: company.name }),
      selectedIds: definition?.selectedOrgNos ?? [],
    });
    this.companyScope = new RadioFieldModel<CustomFieldCompanyScope>({
      value: definition?.companyScope ?? "ALL_COMPANIES",
      options: ["ALL_COMPANIES", "SELECTED_COMPANIES", "HIDDEN"],
      idFn: (scope) => scope,
      labelFn: (scope) => this.scopeLabel(scope),
    });
    this.accountAdminPermission = ownerFieldModel();
    this.adminPermission = permissionFieldModel(definition?.permissions.ADMIN ?? "EDIT");
    this.boardMemberPermission = permissionFieldModel(
      definition?.permissions.BOARD_MEMBER ?? "NONE",
    );
    this.shareholderPermission = permissionFieldModel(
      definition?.permissions.SHAREHOLDER ?? "NONE",
    );
    this.commentRequiredFor = new StringFieldModel({
      value: (definition?.commentRequiredFor ?? []).join(", "),
    });
    this.decimalPlaces = new RadioFieldModel<number>({
      value: definition?.numberFormat?.decimalPlaces ?? defaultNumberFormat.decimalPlaces,
      options: decimalPlaceOptions,
      idFn: (places) => String(places),
      labelFn: (places) => ({ translated: String(places) }),
    });
    this.thousandsSeparator = new BooleanFieldModel({
      value: (definition?.numberFormat ?? defaultNumberFormat).separators !== "NONE",
    });
    this.requiredLength = new NumberFieldModel({
      value: definition?.requiredLength,
      isInteger: true,
      isPositive: true,
    });
    this.limitToRole = new RadioFieldModel<CustomFieldContactRole | undefined>({
      value: definition?.limitToRole,
      options: [...customFieldContactRoles],
      idFn: (role) => role ?? "",
      labelFn: (role) =>
        role ? contactRoleLabels[role] : { key: "entity-data.select-user-group" },
      required: true,
    });
    this.showDescription = Boolean(definition?.description);
    this.archivedOptions = definition?.selectOptions.filter((option) => option.isArchived) ?? [];
    makeAutoObservable(this, {}, { autoBind: true });
    validationEntries(definition?.validations).forEach((entry) =>
      this.pushValidationRow(entry.validation, entry.requirement),
    );
    definition?.selectOptions
      .filter((option) => !option.isArchived)
      .forEach((option) =>
        this.options.push({
          id: String(this.nextOptionId++),
          optionId: option.id,
          label: new StringFieldModel({ value: option.label }),
        }),
      );
    if (this.options.length === 0) {
      this.addOption();
      this.focusOptionId = undefined;
    }
  }

  setActiveTab(tab: AddFieldTab) {
    this.activeTab = tab;
  }

  private scopeLabel(scope: CustomFieldCompanyScope): TMessage {
    switch (scope) {
      case "ALL_COMPANIES":
        return { key: "all-companies" };
      case "SELECTED_COMPANIES":
        return {
          key: "entity-data.scope-selected",
          params: {
            selected: this.companySelection.selectedIds.size,
            total: this.companies.length,
          },
        };
      case "HIDDEN":
        return { key: "hidden" };
    }
  }

  addDescription() {
    this.showDescription = true;
  }

  get availableValidations(): CustomFieldValidation[] {
    return customFieldValidations.filter((validation) => {
      switch (validation) {
        case "REQUIRED_LENGTH":
          return this.dataType === "NUMBER";
        case "LIMIT_SELECTION_TO_ROLE":
          return this.dataType === "CONTACT";
        default:
          return true;
      }
    });
  }

  get unusedValidations(): CustomFieldValidation[] {
    const used = this.validationRows.map((row) => row.validation.value);
    return this.availableValidations.filter((validation) => !used.includes(validation));
  }

  get numberFormat(): CustomFieldNumberFormat | undefined {
    return this.dataType === "NUMBER"
      ? {
          decimalPlaces: this.decimalPlaces.value,
          separators: this.thousandsSeparator.value ? undefined : "NONE",
        }
      : undefined;
  }

  get hasRequiredLength(): boolean {
    return this.validationRows.some((row) => row.validation.value === "REQUIRED_LENGTH");
  }

  get hasLimitToRole(): boolean {
    return this.validationRows.some((row) => row.validation.value === "LIMIT_SELECTION_TO_ROLE");
  }

  get requiredLengthIssue(): RequiredLengthIssue | undefined {
    const format = this.numberFormat;
    return format && this.hasRequiredLength
      ? requiredLengthIssue(format, this.requiredLength.toNumber())
      : undefined;
  }

  get isValid(): boolean {
    return this.requiredLengthIssue?.level !== "error";
  }

  addValidationRow(validation: CustomFieldValidation) {
    this.pushValidationRow(validation, requirementsFor(validation)[0] ?? "OPTIONAL");
  }

  get isCommentConditional(): boolean {
    return this.validationRows.some(
      (row) => row.validation.value === "COMMENT" && row.requirement.value === "CONDITIONAL",
    );
  }

  get commentTriggerValues(): string[] {
    return this.commentRequiredFor.inputValue
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
  }

  removeValidationRow(id: string) {
    this.validationRows = this.validationRows.filter((row) => row.id !== id);
  }

  private pushValidationRow(
    validation: CustomFieldValidation,
    requirement: CustomFieldRequirement,
  ) {
    const id = String(this.nextValidationId++);
    this.validationRows.push({
      id,
      validation: new RadioFieldModel<CustomFieldValidation>({
        value: validation,
        options: () => this.validationOptions(id),
        idFn: (option) => option,
        labelFn: (option) => validationLabels[option],
      }),
      requirement: new RadioFieldModel<CustomFieldRequirement>({
        value: requirement,
        options: () => requirementsFor(validation),
        idFn: (option) => option,
        labelFn: (option) => requirementLabels[option],
      }),
    });
  }

  private validationOptions(rowId: string): CustomFieldValidation[] {
    const takenElsewhere = this.validationRows
      .filter((row) => row.id !== rowId)
      .map((row) => row.validation.value);
    return this.availableValidations.filter((validation) => !takenElsewhere.includes(validation));
  }

  addOption() {
    const id = String(this.nextOptionId++);
    this.options.push({ id, label: new StringFieldModel() });
    this.focusOptionId = id;
  }

  removeOption(id: string) {
    this.options = this.options.filter((option) => option.id !== id);
  }

  moveOption(option: AddFieldOption, toOption: AddFieldOption) {
    const to = this.options.indexOf(toOption);
    const rest = this.options.filter((other) => other !== option);
    if (to === -1 || rest.length === this.options.length) {
      return;
    }
    this.options = [...rest.slice(0, to), option, ...rest.slice(to)];
  }

  setError(error?: TMessage) {
    this.error = error;
  }

  setSubmitting(isSubmitting: boolean) {
    this.isSubmitting = isSubmitting;
  }

  optionLabels(): string[] {
    return this.options.map((option) => option.label.inputValue.trim()).filter(Boolean);
  }

  validate(): boolean {
    this.title.commit();
    if (!this.title.validate()) {
      this.activeTab = "setup";
      return false;
    }
    const labels = this.optionLabels();
    if (this.dataType === "SINGLE_SELECT" && labels.length === 0) {
      this.error = { key: "entity-data.options-required" };
      this.activeTab = "setup";
      return false;
    }
    if (new Set(labels).size !== labels.length) {
      this.error = { key: "entity-data.duplicate-options" };
      this.activeTab = "setup";
      return false;
    }
    if (this.isCommentConditional && this.commentTriggerValues.length === 0) {
      this.error = { key: "entity-data.comment-values-required" };
      this.activeTab = "validation";
      return false;
    }
    if (this.hasRequiredLength) {
      this.requiredLength.commit();
      const length = this.requiredLength.toNumber();
      if (length === undefined || length < 1) {
        this.error = { key: "entity-data.required-length-required" };
        this.activeTab = "validation";
        return false;
      }
      if (this.requiredLengthIssue?.level === "error") {
        this.error = undefined;
        this.activeTab = "validation";
        return false;
      }
    }
    if (this.hasLimitToRole && !this.limitToRole.validate()) {
      this.error = undefined;
      this.activeTab = "validation";
      return false;
    }
    if (this.companyScope.value === "SELECTED_COMPANIES" && this.companySelection.isNoneSelected) {
      this.error = { key: "entity-data.companies-required" };
      this.activeTab = "activation";
      return false;
    }
    this.error = undefined;
    return true;
  }

  toCommand() {
    const description = this.description.inputValue.trim();
    const scope = this.companyScope.value;
    const shared = {
      name: this.title.inputValue.trim(),
      description: this.showDescription && description !== "" ? description : undefined,
      dataType: this.dataType,
      selectOptions: this.dataType === "SINGLE_SELECT" ? this.commandOptions() : [],
      companyScope: scope,
      selectedOrgNos:
        scope === "SELECTED_COMPANIES"
          ? this.companySelection.selected.map((company) => company.orgNo)
          : [],
      permissions: {
        ACCOUNT_ADMIN: "EDIT" as const,
        ADMIN: this.adminPermission.value,
        BOARD_MEMBER: this.boardMemberPermission.value,
        SHAREHOLDER: this.shareholderPermission.value,
      },
      numberFormat: this.numberFormat,
      validations: this.commandValidations(),
      commentRequiredFor: this.isCommentConditional ? this.commentTriggerValues : [],
      requiredLength: this.hasRequiredLength ? this.requiredLength.toNumber() : undefined,
      limitToRole: this.hasLimitToRole ? this.limitToRole.value : undefined,
    };
    return this.definition
      ? { commandType: "UPDATE_FIELD" as const, definitionId: this.definition.id, ...shared }
      : { commandType: "CREATE_FIELD" as const, objectType: this.objectType, ...shared };
  }

  private commandValidations(): CustomFieldValidations {
    return this.validationRows.reduce<CustomFieldValidations>(
      (validations, row) => ({ ...validations, [row.validation.value]: row.requirement.value }),
      {},
    );
  }

  private commandOptions(): { id?: string; label: string; isArchived?: boolean }[] {
    const rows = this.options
      .map((option) => ({ id: option.optionId, label: option.label.inputValue.trim() }))
      .filter((option) => option.label !== "");
    return [...rows, ...this.archivedOptions];
  }
}

export const fetchContactCandidates = (orgNo: string, model: EntityValueFormModel): DCEvent[] => {
  if (model.field.dataType !== "CONTACT") {
    return [];
  }
  return [
    httpGet({
      url: `/custom-fields/${orgNo}/${model.field.definitionId}/contacts`,
      onSuccess: (res: HttpResponse<CustomFieldContact[]>) => {
        model.setContacts(res.data);
        return [];
      },
      onFailure: () => {
        model.setContacts([]);
        return [];
      },
    }),
  ];
};

export const openEditValue = (
  state: EntityDataState,
  field: CompanyCustomField,
  language: Language,
): DCEvent[] => {
  const model = new EntityValueFormModel(field, language);
  return [updateEntityData({ editModel: model }), ...fetchContactCandidates(state.orgNo, model)];
};

export const closeEditValue = (): DCEvent => updateEntityData({ editModel: undefined });

export const submitEditValue = (state: EntityDataState): DCEvent[] => {
  const model = state.editModel;
  const subject = state.subject;
  if (!model || subject === undefined || !model.validate()) {
    return [];
  }
  model.setSubmitting(true);
  return [
    httpPost({
      url: `/custom-fields/${state.orgNo}/commands`,
      data: setValueCommand(model.field.definitionId, subject, model.toInput()),
      onSuccess: () => [
        closeEditValue(),
        fetchFields(state.orgNo),
        ...panelLogRefresh(state, model.field.definitionId),
      ],
      onFailure: (err) => {
        model.setSubmitting(false);
        model.setError(commandError(err));
        return [];
      },
    }),
  ];
};

const updateFieldPanel = (
  definitionId: string,
  partial: Partial<CustomFieldPanelState>,
): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    entityData: (entityData) => {
      const panel = entityData?.fieldPanel;
      if (!entityData || panel?.origin !== "CUSTOM" || panel.definitionId !== definitionId) {
        return entityData;
      }
      return { ...entityData, fieldPanel: { ...panel, ...partial } };
    },
  },
});

const updateSystemFieldPanel = (
  systemField: SystemFieldKey,
  partial: Partial<SystemFieldPanelState>,
): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    entityData: (entityData) => {
      const panel = entityData?.fieldPanel;
      if (!entityData || panel?.origin !== "SYSTEM" || panel.systemField !== systemField) {
        return entityData;
      }
      return { ...entityData, fieldPanel: { ...panel, ...partial } };
    },
  },
});

const setPanelTab = (tab: EntityPanelTab): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    entityData: (entityData) => {
      const panel = entityData?.fieldPanel;
      if (!entityData || !panel) {
        return entityData;
      }
      return { ...entityData, fieldPanel: { ...panel, tab } };
    },
  },
});

const fetchPanelLog = (orgNo: string, definitionId: string): DCEvent =>
  httpGet({
    url: `/custom-fields/${orgNo}/${definitionId}/history`,
    onSuccess: (res: HttpResponse<CustomFieldEventEntry[]>) => [
      updateFieldPanel(definitionId, { logResult: ok(res.data) }),
    ],
    onFailure: () => [updateFieldPanel(definitionId, { logResult: failure() })],
  });

const fetchSystemFieldLog = (orgNo: string, systemField: SystemFieldKey): DCEvent =>
  httpGet({
    url: `/custom-fields/${orgNo}/system-fields/${systemField}/history`,
    onSuccess: (res: HttpResponse<SystemFieldLogEntry[]>) => [
      updateSystemFieldPanel(systemField, { logResult: ok(res.data) }),
    ],
    onFailure: () => [updateSystemFieldPanel(systemField, { logResult: failure() })],
  });

const systemPanelLogRefresh = (state: EntityDataState): DCEvent[] => {
  const panel = state.fieldPanel;
  return panel?.origin === "SYSTEM" && hasSystemFieldLog(panel.systemField)
    ? [fetchSystemFieldLog(state.orgNo, panel.systemField)]
    : [];
};

const panelLogRefresh = (state: EntityDataState, definitionId: string): DCEvent[] => {
  const panel = state.fieldPanel;
  return panel?.origin === "CUSTOM" && panel.definitionId === definitionId
    ? [fetchPanelLog(state.orgNo, definitionId)]
    : [];
};

const openFieldPanel = (state: EntityDataState, row: EntityFieldRow): DCEvent[] => {
  if (row.origin === "SYSTEM") {
    const systemField = row.field.systemField;
    return [
      updateEntityData({
        fieldPanel: { origin: "SYSTEM", systemField, tab: "field", logResult: pending() },
      }),
      ...(hasSystemFieldLog(systemField) ? [fetchSystemFieldLog(state.orgNo, systemField)] : []),
    ];
  }
  const field = row.field;
  return [
    updateEntityData({
      fieldPanel: {
        origin: "CUSTOM",
        definitionId: field.definitionId,
        tab: "field",
        logResult: pending(),
      },
    }),
    fetchPanelLog(state.orgNo, field.definitionId),
  ];
};

const closeFieldPanel = (): DCEvent => updateEntityData({ fieldPanel: undefined });

export type EntityLogRow = {
  id: string;
  label: TMessage;
  actorName: string;
  recordedAt: string;
  value?: CustomFieldValue;
};

export const customFieldEventLabel = (
  entry: CustomFieldEventEntry,
  companyName?: string,
): TMessage => {
  const payload = entry.payload;
  switch (payload.payloadType) {
    case "VALUE_SET":
      return companyName === undefined
        ? { key: payload.value ? "entity-data.value-updated" : "entity-data.value-cleared" }
        : {
            key: payload.value
              ? "entity-data.event-value-updated"
              : "entity-data.event-value-cleared",
            params: { company: companyName },
          };
    case "FIELD_CREATED":
      return { key: "entity-data.event-field-created" };
    case "FIELD_RENAMED":
      return { key: "entity-data.event-field-renamed", params: { name: payload.name } };
    case "DESCRIPTION_CHANGED":
      return { key: "entity-data.event-description-changed" };
    case "DATA_TYPE_CHANGED":
      return {
        key: "entity-data.event-type-changed",
        params: { type: dataTypeLabels[payload.dataType] },
      };
    case "OPTION_ADDED":
      return { key: "entity-data.event-option-added", params: { label: payload.option.label } };
    case "OPTION_RELABELLED":
      return { key: "entity-data.event-option-relabelled", params: { label: payload.label } };
    case "OPTION_ARCHIVED":
      return { key: "entity-data.event-option-archived" };
    case "OPTION_RESTORED":
      return { key: "entity-data.event-option-restored" };
    case "OPTION_REMOVED":
      return { key: "entity-data.event-option-removed" };
    case "OPTIONS_REORDERED":
      return { key: "entity-data.event-options-reordered" };
    case "NUMBER_FORMAT_CHANGED":
      return { key: "entity-data.event-formatting-changed" };
    case "VALIDATIONS_CHANGED":
      return { key: "entity-data.event-validations-changed" };
    case "PERMISSIONS_CHANGED":
      return { key: "entity-data.event-permissions-changed" };
    case "ACTIVATION_CHANGED":
      return { key: "entity-data.event-activation-changed" };
    case "FIELD_ARCHIVED":
      return { key: "entity-data.event-field-archived" };
  }
};

const loggedSystemFields: SystemFieldKey[] = [
  "COMPANY_NAME",
  "ORGANISATION_NUMBER",
  "BUSINESS_ADDRESS",
  "POSTAL_ADDRESS",
  "DATE_OF_INCORPORATION",
  "TIN_NUMBER",
];

export const hasSystemFieldLog = (systemField: SystemFieldKey): boolean =>
  loggedSystemFields.includes(systemField);

export type SystemFieldLogRow = {
  id: string;
  date: string;
  change: TMessage;
  actorName: string;
};

export const systemFieldLogRows = (
  entries: SystemFieldLogEntry[],
  lang: Language,
): SystemFieldLogRow[] =>
  entries.map((entry) => ({
    id: entry.id,
    date: entry.recordedAt,
    change: systemFieldChangeLabel(entry, lang),
    actorName: entry.actorName,
  }));

const systemFieldChangeLabel = (entry: SystemFieldLogEntry, lang: Language): TMessage => {
  const date = entry.effectiveFrom ? formatDate(lang, entry.effectiveFrom) : undefined;
  const from = entry.fromLabel?.[lang] ?? entry.from;
  const to = entry.toLabel?.[lang] ?? entry.to;
  if (!to) {
    return date
      ? { key: "entity-data.log-cleared-effective", params: { from: from ?? "", date } }
      : { key: "entity-data.log-cleared", params: { from: from ?? "" } };
  }
  if (!from) {
    return date
      ? { key: "entity-data.log-set-effective", params: { to, date } }
      : { key: "entity-data.log-set", params: { to } };
  }
  const values = { from, to };
  return date
    ? { key: "entity-data.log-updated-effective", params: { ...values, date } }
    : { key: "entity-data.log-updated", params: values };
};

export const entityLogRows = (entries: CustomFieldEventEntry[]): EntityLogRow[] =>
  entries.map((entry) => ({
    id: entry.id,
    label: customFieldEventLabel(entry),
    actorName: entry.actor.name,
    recordedAt: entry.recordedAt,
    value: entry.payload.payloadType === "VALUE_SET" ? entry.payload.value : undefined,
  }));

export const openValueHistory = (state: EntityDataState, field: CompanyCustomField): DCEvent[] => [
  updateEntityData({ history: { field, result: pending() } }),
  httpGet({
    url: `/custom-fields/${state.orgNo}/${field.definitionId}/history`,
    onSuccess: (res: HttpResponse<CustomFieldEventEntry[]>) => [
      updateEntityData({ history: { field, result: ok(res.data) } }),
    ],
    onFailure: () => [updateEntityData({ history: { field, result: failure() } })],
  }),
];

export const closeValueHistory = (): DCEvent => updateEntityData({ history: undefined });

export const openAddressEdit = (field: CompanySystemField, locale: Language): DCEvent[] => {
  const addressType = systemFieldAddressType(field);
  return addressType && field.details?.detailsType === "ADDRESS"
    ? [updateEntityData({ addressForm: newAddressForm(addressType, field.details, locale) })]
    : [];
};

export const closeAddressEdit = (): DCEvent => updateEntityData({ addressForm: undefined });

export const saveAddress = (state: EntityDataState): DCEvent[] => {
  const form = state.addressForm;
  if (!form) {
    return [];
  }
  return [
    settingsApi.updateAddress({
      orgNo: state.orgNo,
      request: toUpdateAddressRequest(form),
      onSuccess: () => [
        closeAddressEdit(),
        showSuccess(addressUpdatedMessage(form.addressType), { autoHide: true }),
        fetchFields(state.orgNo),
        ...systemPanelLogRefresh(state),
      ],
      onFailure: () => [showGeneralError()],
    }),
  ];
};

type EntityFieldPanelCommon = {
  id: string;
  name: string;
  description?: string;
  tab: EntityPanelTab;
  onSelectTab: (tab: EntityPanelTab) => void;
  onClose: () => void;
};

export type EntityFieldPanelViewProps =
  | (EntityFieldPanelCommon & {
      origin: "CUSTOM";
      field: CompanyCustomField;
      logResult: Result<CustomFieldEventEntry[]>;
      logRows: EntityLogRow[];
      onConfigureField?: () => void;
    })
  | (EntityFieldPanelCommon & {
      origin: "SYSTEM";
      field: CompanySystemField;
      onEdit?: () => void;
      hasLog: boolean;
      logResult: Result<SystemFieldLogEntry[]>;
      logRows: SystemFieldLogRow[];
      detailRows: SystemFieldDetailRow[];
      detailLink?: { label: TMessage; onClick: () => void };
    });

export type EntityDataViewProps = {
  appBar: AppBarData | null;
  alertStore: AlertMessageStore;
  tab: EntityDataTab;
  searchModel: StringFieldModel;
  fieldsResult: Result<CompanyCustomField[]>;
  rows: EntityFieldRow[];
  hasSystemFields: boolean;
  accountName?: string;
  companyName?: string;
  editModel?: EntityValueFormModel;
  addressForm?: AddressForm;
  history?: EntityDataHistoryState;
  fieldPanel?: EntityFieldPanelViewProps;
  allowEdit: boolean;
  uploadConfig?: FileUploadConfig;
  isImporting?: boolean;
  importResult?: CustomFieldsImportResult;
  onConfigureFields?: () => void;
  onSelectTab: (tab: EntityDataTab) => void;
  onSelectField: (row: EntityFieldRow) => void;
  onEditValue: (field: CompanyCustomField) => void;
  onCloseEdit: () => void;
  onSubmitEdit: () => void;
  onShowHistory: (field: CompanyCustomField) => void;
  onCloseHistory: () => void;
  onCloseAddressEdit: () => void;
  onSaveAddress: () => void;
  onExportExcel: () => void;
  onUploadExcel: (files: File[] | null) => void;
  onCloseImportResult: () => void;
};

const prepFieldPanel = (
  state: EntityDataState,
  dispatch: EventDispatcher,
  t: TFunction,
  lang: Language,
  allowAddressEdit: boolean,
): EntityFieldPanelViewProps | undefined => {
  const panel = state.fieldPanel;
  if (!panel) {
    return undefined;
  }
  const common = {
    tab: panel.tab,
    onSelectTab: (tab: EntityPanelTab) => dispatch([setPanelTab(tab)]),
    onClose: () => dispatch([closeFieldPanel()]),
  };
  if (panel.origin === "SYSTEM") {
    const field = state.systemFields.find((f) => f.systemField === panel.systemField);
    if (!field) {
      return undefined;
    }
    const detailLinkUrl = systemFieldDetailLinkUrl(field, state.orgNo);
    return {
      ...common,
      origin: "SYSTEM",
      id: systemRowId(field.systemField),
      field,
      onEdit:
        allowAddressEdit && systemFieldAddressType(field)
          ? () => dispatch(openAddressEdit(field, lang))
          : undefined,
      name: t(systemFieldLabels[field.systemField]),
      description: t(systemFieldDescriptions[field.systemField]),
      hasLog: hasSystemFieldLog(field.systemField),
      logResult: panel.logResult,
      logRows: isOk(panel.logResult) ? systemFieldLogRows(panel.logResult.data, lang) : [],
      detailRows: systemFieldDetailRows(field),
      detailLink: detailLinkUrl
        ? {
            label: { key: "entity-data.system-detail-view-details" },
            onClick: () => dispatch([navigate(detailLinkUrl)]),
          }
        : undefined,
    };
  }
  if (!isOk(state.fieldsResult)) {
    return undefined;
  }
  const field = state.fieldsResult.data.find((f) => f.definitionId === panel.definitionId);
  if (!field) {
    return undefined;
  }
  const configureUrl = configureFieldsUrl(state, field.definitionId);
  return {
    ...common,
    origin: "CUSTOM",
    id: field.definitionId,
    field,
    name: field.name,
    description: field.description,
    logResult: panel.logResult,
    logRows: isOk(panel.logResult) ? entityLogRows(panel.logResult.data) : [],
    onConfigureField: configureUrl
      ? () => dispatch([navigate(configureUrl, { state: configureFieldsNavigation(state) })])
      : undefined,
  };
};

export const withoutFieldEditing = (state: EntityDataState): EntityDataState =>
  isOk(state.fieldsResult)
    ? {
        ...state,
        fieldsResult: ok(state.fieldsResult.data.map((field) => ({ ...field, canEdit: false }))),
      }
    : state;

export const prepEntityDataView = ({
  state = defaultEntityDataState("unknown"),
  allowEdit,
  allowAddressEdit,
  appBar,
  alertStore,
  dispatch,
  t,
  lang,
  accountName,
  companyName,
}: {
  state: EntityDataState | undefined;
  allowEdit: boolean;
  allowAddressEdit: boolean;
  appBar: AppBarData | null;
  alertStore: AlertMessageStore;
  dispatch: EventDispatcher;
  t: TFunction;
  lang: Language;
  accountName?: string;
  companyName?: string;
}): EntityDataViewProps => {
  const gatedState = allowEdit ? state : withoutFieldEditing(state);
  const configureUrl = configureFieldsUrl(gatedState);
  return {
    appBar,
    alertStore,
    tab: gatedState.tab,
    searchModel: gatedState.searchModel,
    fieldsResult: gatedState.fieldsResult,
    rows: entityFieldRows(gatedState, t, lang),
    hasSystemFields: gatedState.systemFields.length > 0,
    accountName,
    companyName,
    editModel: gatedState.editModel,
    addressForm: allowAddressEdit ? gatedState.addressForm : undefined,
    history: gatedState.history,
    fieldPanel: prepFieldPanel(gatedState, dispatch, t, lang, allowAddressEdit),
    allowEdit,
    uploadConfig: gatedState.uploadConfig,
    isImporting: gatedState.isImporting,
    importResult: gatedState.importResult,
    onConfigureFields: configureUrl
      ? () => dispatch([navigate(configureUrl, { state: configureFieldsNavigation(gatedState) })])
      : undefined,
    onSelectTab: (tab) => dispatch([updateEntityData({ tab })]),
    onSelectField: (row) => dispatch(openFieldPanel(gatedState, row)),
    onEditValue: (field) => dispatch(openEditValue(state, field, lang)),
    onCloseEdit: () => dispatch([closeEditValue()]),
    onSubmitEdit: () => dispatch(submitEditValue(gatedState)),
    onShowHistory: (field) => dispatch(openValueHistory(gatedState, field)),
    onCloseHistory: () => dispatch([closeValueHistory()]),
    onCloseAddressEdit: () => dispatch([closeAddressEdit()]),
    onSaveAddress: () => dispatch(saveAddress(gatedState)),
    onExportExcel: () => dispatch([exportEntityDataToExcel(gatedState.orgNo)]),
    onUploadExcel: (files) => dispatch(uploadEntityDataExcel(gatedState, files)),
    onCloseImportResult: () => dispatch([updateEntityData({ importResult: undefined })]),
  };
};

export type CustomFieldImportProblem =
  | "UNKNOWN_COLUMN"
  | "MISSING_ENTITY_ID"
  | "UNKNOWN_COMPANY"
  | "DUPLICATE_COMPANY"
  | "DUPLICATE_FIELD"
  | "INVALID_NUMBER"
  | "INVALID_DATE"
  | "INVALID_COUNTRY_CODE"
  | "INVALID_ORGANISATION_NUMBER"
  | "CONTACT_NOT_IMPORTABLE"
  | "UNKNOWN_OPTION"
  | "ARCHIVED_OPTION"
  | "FIELD_NOT_ACTIVATED"
  | "NOT_PERMITTED"
  | "REQUIRED_DATE_MISSING"
  | "DATE_NOT_CAPTURED"
  | "VALID_TO_BEFORE_EFFECTIVE_FROM"
  | "DATES_REQUIRE_VALUE"
  | "COMMENT_REQUIRES_VALUE"
  | "COMMENT_REQUIRED"
  | "COMMENT_NOT_CAPTURED"
  | "COMMENT_TOO_LONG"
  | "WRONG_COMPANY"
  | "MISSING_REQUIRED_COLUMN"
  | "UNKNOWN_ROLE"
  | "ROLE_NOT_ACTIVATED"
  | "INVALID_NAME"
  | "INVALID_EMAIL"
  | "INVALID_PHONE_NUMBER"
  | "INVALID_NATIONAL_ID"
  | "INVALID_D_NUMBER"
  | "INCOMPLETE_ADDRESS"
  | "AMBIGUOUS_PERSON"
  | "AMBIGUOUS_ROLE"
  | "PERIOD_CONFLICT"
  | "PERIOD_OVERLAP"
  | "PERSON_VALUE_CONFLICT"
  | "DUPLICATE_APPOINTMENT"
  | "DATE_AFTER_TODAY"
  | "UNKNOWN_START_NOT_ENDED"
  | "REJECTED"
  | "MISSING_VALUE"
  | "INVALID_OWNERSHIP_SHARE"
  | "NOT_OWNERSHIP_REGISTER"
  | "SEVERAL_SHARE_CLASSES"
  | "UNKNOWN_OWNER"
  | "AMBIGUOUS_OWNER"
  | "OWNER_IS_COMPANY"
  | "OWNERS_EXCEED_TOTAL"
  | "OTHER_ROW_REJECTED"
  | "MISSING_START_DATE"
  | "STATUS_CONFLICT"
  | "OWNER_NOT_ENDED"
  | "INVALID_COMPANY_STATUS";

export type CustomFieldImportIssue = {
  problem: CustomFieldImportProblem;
  row?: number;
  column?: string;
  value?: string;
};

export type CustomFieldsImportResult = {
  updated: number;
  cleared: number;
  unchanged: number;
  issues: CustomFieldImportIssue[];
};

export type ImportOutcome = "changed" | "nothing-changed" | "no-data" | "wrong-company";

export const importOutcome = (result: CustomFieldsImportResult): ImportOutcome => {
  if (result.issues.some((issue) => issue.problem === "WRONG_COMPANY")) return "wrong-company";
  if (result.updated + result.cleared > 0) return "changed";
  if (result.unchanged === 0 && result.issues.length === 0) return "no-data";
  return "nothing-changed";
};

export const importOutcomeWarnings: Record<Exclude<ImportOutcome, "changed">, TMessage> = {
  "nothing-changed": { key: "entity-data.import-nothing-changed" },
  "no-data": { key: "entity-data.import-no-data" },
  "wrong-company": { key: "entity-data.import-wrong-company" },
};

export const importProblemLabels: Record<CustomFieldImportProblem, TMessage> = {
  UNKNOWN_COLUMN: { key: "entity-data.import-problem-unknown-column" },
  MISSING_ENTITY_ID: { key: "entity-data.import-problem-missing-entity-id" },
  UNKNOWN_COMPANY: { key: "entity-data.import-problem-unknown-company" },
  DUPLICATE_COMPANY: { key: "entity-data.import-problem-duplicate-company" },
  DUPLICATE_FIELD: { key: "entity-data.import-problem-duplicate-field" },
  INVALID_NUMBER: { key: "entity-data.import-problem-invalid-number" },
  INVALID_DATE: { key: "entity-data.import-problem-invalid-date" },
  INVALID_COUNTRY_CODE: { key: "entity-data.import-problem-invalid-country-code" },
  INVALID_ORGANISATION_NUMBER: { key: "entity-data.import-problem-invalid-organisation-number" },
  CONTACT_NOT_IMPORTABLE: { key: "entity-data.import-problem-contact-not-importable" },
  UNKNOWN_OPTION: { key: "entity-data.import-problem-unknown-option" },
  ARCHIVED_OPTION: { key: "entity-data.import-problem-archived-option" },
  FIELD_NOT_ACTIVATED: { key: "entity-data.import-problem-field-not-activated" },
  NOT_PERMITTED: { key: "entity-data.import-problem-not-permitted" },
  REQUIRED_DATE_MISSING: { key: "entity-data.import-problem-required-date-missing" },
  DATE_NOT_CAPTURED: { key: "entity-data.import-problem-date-not-captured" },
  VALID_TO_BEFORE_EFFECTIVE_FROM: {
    key: "entity-data.import-problem-valid-to-before-effective-from",
  },
  DATES_REQUIRE_VALUE: { key: "entity-data.import-problem-dates-require-value" },
  COMMENT_REQUIRES_VALUE: { key: "entity-data.import-problem-comment-requires-value" },
  COMMENT_REQUIRED: { key: "entity-data.import-problem-comment-required" },
  COMMENT_NOT_CAPTURED: { key: "entity-data.import-problem-comment-not-captured" },
  COMMENT_TOO_LONG: { key: "entity-data.import-problem-comment-too-long" },
  WRONG_COMPANY: { key: "entity-data.import-problem-wrong-company" },
  MISSING_REQUIRED_COLUMN: { key: "entity-data.import-problem-missing-required-column" },
  UNKNOWN_ROLE: { key: "entity-data.import-problem-unknown-role" },
  ROLE_NOT_ACTIVATED: { key: "entity-data.import-problem-role-not-activated" },
  INVALID_NAME: { key: "entity-data.import-problem-invalid-name" },
  INVALID_EMAIL: { key: "entity-data.import-problem-invalid-email" },
  INVALID_PHONE_NUMBER: { key: "entity-data.import-problem-invalid-phone-number" },
  INVALID_NATIONAL_ID: { key: "entity-data.import-problem-invalid-national-id" },
  INVALID_D_NUMBER: { key: "entity-data.import-problem-invalid-d-number" },
  INCOMPLETE_ADDRESS: { key: "entity-data.import-problem-incomplete-address" },
  AMBIGUOUS_PERSON: { key: "entity-data.import-problem-ambiguous-person" },
  AMBIGUOUS_ROLE: { key: "entity-data.import-problem-ambiguous-role" },
  PERIOD_CONFLICT: { key: "entity-data.import-problem-period-conflict" },
  PERIOD_OVERLAP: { key: "entity-data.import-problem-period-overlap" },
  PERSON_VALUE_CONFLICT: { key: "entity-data.import-problem-person-value-conflict" },
  DUPLICATE_APPOINTMENT: { key: "entity-data.import-problem-duplicate-appointment" },
  DATE_AFTER_TODAY: { key: "entity-data.import-problem-date-after-today" },
  UNKNOWN_START_NOT_ENDED: { key: "entity-data.import-problem-unknown-start-not-ended" },
  REJECTED: { key: "entity-data.import-problem-rejected" },
  MISSING_VALUE: { key: "entity-data.import-problem-missing-value" },
  INVALID_OWNERSHIP_SHARE: { key: "entity-data.import-problem-invalid-ownership-share" },
  NOT_OWNERSHIP_REGISTER: { key: "entity-data.import-problem-not-ownership-register" },
  SEVERAL_SHARE_CLASSES: { key: "entity-data.import-problem-several-share-classes" },
  UNKNOWN_OWNER: { key: "entity-data.import-problem-unknown-owner" },
  AMBIGUOUS_OWNER: { key: "entity-data.import-problem-ambiguous-owner" },
  OWNER_IS_COMPANY: { key: "entity-data.import-problem-owner-is-company" },
  OWNERS_EXCEED_TOTAL: { key: "entity-data.import-problem-owners-exceed-total" },
  OTHER_ROW_REJECTED: { key: "entity-data.import-problem-other-row-rejected" },
  MISSING_START_DATE: { key: "entity-data.import-problem-missing-start-date" },
  STATUS_CONFLICT: { key: "entity-data.import-problem-status-conflict" },
  OWNER_NOT_ENDED: { key: "entity-data.import-problem-owner-not-ended" },
  INVALID_COMPANY_STATUS: { key: "entity-data.import-problem-invalid-company-status" },
};
